// Spectator self-registration (132) -- the click-through page for the
// confirmation email sent to a parent's on-file address. POSTs the token to
// confirm the link; no sign-in required (the token is the credential).

import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

export default function LinkConfirmPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<"working" | "ok" | "error">("working");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setState("error");
      return;
    }
    api
      .confirmParentLink(token)
      .then(() => setState("ok"))
      .catch(() => setState("error"));
  }, [token]);

  return (
    <div className="mx-auto max-w-md p-6" data-page="link-confirm" data-state={state}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {state === "working" && <Loader2 className="h-5 w-5 animate-spin" />}
            {state === "ok" && <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            {state === "error" && <AlertTriangle className="h-5 w-5 text-destructive" />}
            {state === "working" ? "Confirming..." : state === "ok" ? "Link confirmed" : "Link not confirmed"}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {state === "working" && <p>Please wait while we confirm the request.</p>}
          {state === "ok" && (
            <p>
              The parent link is confirmed. The child now appears in the
              requester's account. <Link className="text-primary underline" to="/">Continue</Link>.
            </p>
          )}
          {state === "error" && (
            <p>
              This confirmation link is invalid or has expired. Ask the person who
              requested the link to try again.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
